import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { AllMedicalCodeTypes, UserFileTypesLookup } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useUploadUserFile } from '@eluve/smart-blocks';

// Status types
type UploadStatus =
  | 'idle'
  | 'selecting'
  | 'uploading'
  | 'processing'
  | 'success'
  | 'error';

// Results of file processing
type ProcessingResults = {
  processed: number;
  skipped: number;
  errors: string[];
} | null;

// Dialog component for upload status
const UploadingDialog: React.FC<{
  status: UploadStatus;
  isOpen: boolean;
  onClose: () => void;
  results: ProcessingResults;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}> = ({ status, isOpen, onClose, results, inputRef }) => {
  const { t } = useTranslation();

  const handleFileSelect = () => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tenantAdmin.codes.uploadTitle')}</DialogTitle>
        </DialogHeader>
        <Stack align="center" justify="center" className="py-6" gap={4}>
          {status === 'selecting' && (
            <Stack align="center" className="text-center" gap={2}>
              <Text className="mb-4">
                {t('tenantAdmin.codes.uploadInstructions')}
              </Text>
              <ul className="mb-4 list-disc space-y-1 pl-6 text-left text-sm">
                <li>
                  <strong>Code</strong> -{' '}
                  {t('tenantAdmin.codes.uploadColumnCode')}
                </li>
                <li>
                  <strong>Type</strong> -{' '}
                  {t('tenantAdmin.codes.uploadColumnType', {
                    types: AllMedicalCodeTypes.join(', '),
                  })}
                </li>
                <li>
                  <strong>Description</strong> -{' '}
                  {t('tenantAdmin.codes.uploadColumnDescription')}
                </li>
                <li>
                  <strong>Price</strong> -{' '}
                  {t('tenantAdmin.codes.uploadColumnPrice')}
                </li>
                <li>
                  <strong>Modifiers</strong> -{' '}
                  {t('tenantAdmin.codes.uploadColumnModifiers')}
                </li>
              </ul>
              <Text className="mb-4 text-text-negative">
                {t('tenantAdmin.codes.uploadWarning')}
              </Text>
              <Button onClick={handleFileSelect}>
                {t('tenantAdmin.codes.selectFile')}
              </Button>
              <Text color="disabled" size="xs">
                {t('tenantAdmin.codes.uploadFileLimits')}
              </Text>
            </Stack>
          )}
          {status === 'uploading' && (
            <>
              <LoadingIcon
                size="2xl"
                className="mb-4 text-bg-accent-strong-primary"
              />
              <Text>{t('tenantAdmin.codes.uploadingFile')}</Text>
            </>
          )}
          {status === 'processing' && (
            <>
              <LoadingIcon
                size="2xl"
                className="mb-4 text-bg-accent-strong-primary"
              />
              <Text>{t('tenantAdmin.codes.processingCodes')}</Text>
            </>
          )}
          {status === 'success' && results && (
            <Stack align="center" gap={2}>
              <Stack
                className="mb-4"
                direction="horizontal"
                align="center"
                justify="center"
                fullWidth={false}
              >
                <Icon
                  name="CheckCircle2"
                  size="2xl"
                  className="text-text-positive"
                />
              </Stack>
              <Text className="mb-4 text-center">
                {t('tenantAdmin.codes.uploadComplete')}
              </Text>
              <Stack className="text-sm" gap={2}>
                <Stack justify="between" gap={2} direction="horizontal">
                  <span>{t('tenantAdmin.codes.successfullyProcessed')}</span>
                  <span className="font-medium">
                    {t('tenantAdmin.codes.codesCount', {
                      count: results.processed,
                    })}
                  </span>
                </Stack>
                <Stack justify="between" gap={2} direction="horizontal">
                  <span>{t('tenantAdmin.codes.skipped')}</span>
                  <span className="font-medium">
                    {t('tenantAdmin.codes.codesCount', {
                      count: results.skipped,
                    })}
                  </span>
                </Stack>
                {results.errors.length > 0 && (
                  <div>
                    <Text className="mt-2 mb-1">
                      {t('tenantAdmin.codes.errors')}
                    </Text>
                    <div className="max-h-32 overflow-y-auto rounded-sm p-2 text-xs">
                      {results.errors.slice(0, 5).map((error, i) => (
                        <div key={i} className="mb-1">
                          {error}
                        </div>
                      ))}
                      {results.errors.length > 5 && (
                        <div className="text-text-supporting">
                          {t('tenantAdmin.codes.moreErrors', {
                            count: results.errors.length - 5,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Stack>
            </Stack>
          )}
          {status === 'error' && (
            <>
              <Icon
                name="AlertCircle"
                size="2xl"
                className="mb-4 text-text-negative"
              />
              <Text className="mb-4 text-center">
                {t('tenantAdmin.codes.uploadFailed')}
              </Text>
              {results?.errors && results.errors.length > 0 && (
                <Stack gap={2} className="text-xs text-text-negative">
                  {results.errors.slice(0, 5).map((error, i) => (
                    <div key={i}>{error}</div>
                  ))}
                  {results.errors.length > 5 && (
                    <div className="text-text-supporting">
                      {t('tenantAdmin.codes.moreErrors', {
                        count: results.errors.length - 5,
                      })}
                    </div>
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export const UploadCodesButton: React.FC<{ onSuccess?: () => void }> = ({
  onSuccess,
}) => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };
  const logger = useNamedLogger('UploadCodesButton');

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [processingResults, setProcessingResults] =
    useState<ProcessingResults>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiClient = useApiClient();
  const { uploadUserFileToGCS } = useUploadUserFile(tenantId);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      // Check if file is CSV
      if (!file.name.toLowerCase().endsWith('.csv')) {
        logger.error('File must be a CSV file');
        setUploadStatus('error');
        return;
      }

      try {
        // Update status to uploading
        setUploadStatus('uploading');

        const fileExt = file.name.split('.').pop() ?? 'csv';

        const userUploadId = await uploadUserFileToGCS({
          file,
          mimeType: 'text/csv',
          extension: fileExt,
          fileType: UserFileTypesLookup.MEDICAL_CODE_CONFIGS,
        });

        if (!userUploadId) {
          throw new Error('Failed to upload file');
        }

        setUploadStatus('processing');
        const processingResponse =
          await apiClient.medicalCodes.processMedicalCodesFile({
            params: { tenantId, userGroupId },
            body: {
              userUploadId,
            },
          });

        if (processingResponse.status !== 200) {
          throw new Error('Failed to process uploaded file');
        }

        setProcessingResults(processingResponse.body);

        if (!processingResponse.body.success) {
          setUploadStatus('error');
        } else {
          onSuccess?.();
          setUploadStatus('success');
        }
      } catch (error) {
        logger.error('Error uploading medical codes:', { error });
        setUploadStatus('error');
      }
    },
    [apiClient, tenantId, userGroupId, logger, uploadUserFileToGCS],
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }

      if (e.target) {
        e.target.value = '';
      }
    },
    [handleFileUpload],
  );

  // Start upload process
  const startUpload = useCallback(() => {
    setProcessingResults(null);
    setUploadStatus('selecting');
    setShowUploadDialog(true);
  }, []);

  const handleCloseUploadDialog = useCallback(() => {
    setShowUploadDialog(false);
    setUploadStatus('idle');
  }, []);

  return (
    <>
      <Button onClick={startUpload}>
        {t('tenantAdmin.codes.uploadCodes')}
      </Button>

      {/* Hidden file input for CSV upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Upload Status Dialog */}
      <UploadingDialog
        status={uploadStatus}
        isOpen={showUploadDialog}
        onClose={handleCloseUploadDialog}
        results={processingResults}
        inputRef={fileInputRef}
      />
    </>
  );
};
